import './preview-panel.css';
import React, { useEffect, useRef, useState } from 'react';
import Background from './background';
import { useScale } from './hooks/useScale';

type Props = {
    url: string;
    iframeBackgroundColor?: string;
    backgroundImageUrl: string;
    backgroundHeight: number;
    backgroundWidth: number;
    backgroundEnabled?: boolean;
    height: number;
    width: number;
    top: number;
    left: number;
    title: string;
    ratio?: string;
    disableLinks?: boolean;
    verify?: boolean;
    loadingBackgroundImageUrl?: string;
    spinner?: React.ComponentType<{ complete: boolean }>;
};

function PreviewPanel(props: Props) {
    const {
        url,
        backgroundImageUrl,
        iframeBackgroundColor = 'transparent',
        backgroundHeight,
        backgroundWidth,
        backgroundEnabled = true,
        height,
        width,
        top,
        left,
        title,
        ratio,
        disableLinks = false,
        verify = false,
        loadingBackgroundImageUrl,
        spinner: Spinner,
    } = props;

    const inputEl = useRef<HTMLDivElement>(null);
    const iframeEl = useRef<HTMLIFrameElement>(null);

    const [loading, setLoading] = useState<boolean>(true);
    const [retryCount, setRetryCount] = useState(1);
    const scale = useScale(inputEl, backgroundWidth, backgroundHeight);
    const collapsedScale = useScale(inputEl, width, height);

    useEffect(() => {
        function onMessageHandler(event: MessageEvent<{}>) {
            if (event.origin.indexOf('finn.no') > -1 || event.origin.indexOf('localhost') > -1) {
                const validMessage = iframeEl.current && event.source === iframeEl.current.contentWindow;
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : null;
                if (validMessage && data) {
                    if (data.match === true) {
                        setLoading(false);
                    }
                }
            }
        }

        window.addEventListener('message', onMessageHandler);

        return () => {
            window.removeEventListener('message', onMessageHandler);
        };
    });

    const backgroundImage = loadingBackgroundImageUrl && loading ? loadingBackgroundImageUrl : backgroundImageUrl;

    let loadingAnimation = null;
    if (Spinner) {
        loadingAnimation = <Spinner complete={!loading} />;
    } else if (!loadingBackgroundImageUrl && loading) {
        loadingAnimation = (
            <div
                className="skeleton-screen"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                }}></div>
        );
    }

    const veryfyFrame = () => {
        if (loading === true && retryCount < 4) {
            setRetryCount((count) => count + 1);
        }
    };

    return (
        <div
            className={`preview-panel ${disableLinks ? 'disable-links' : ''}`}
            ref={inputEl}
            style={{
                maxWidth: backgroundWidth,
                maxHeight: backgroundEnabled ? backgroundHeight : height * collapsedScale,
                overflow: 'hidden',
            }}>
            <Background
                backgroundWidth={backgroundEnabled ? backgroundWidth : width}
                backgroundHeight={backgroundEnabled ? backgroundHeight : height}
                backgroundImageUrl={backgroundEnabled ? backgroundImage : ''}
                ratio={ratio}
                enabled={backgroundEnabled}>
                <div
                    style={{
                        position: 'relative',
                        transform: `scale(${backgroundEnabled ? scale : collapsedScale})`,
                        transformOrigin: 'top left',
                        zIndex: 2,
                    }}>
                    <div
                        style={{
                            width: width,
                            height: height,
                            transform: `translate(${backgroundEnabled ? left : 0}px, ${backgroundEnabled ? top : 0}px)`,
                            transformOrigin: 'top left',
                        }}>
                        {loadingAnimation}
                        <iframe
                            ref={iframeEl}
                            scrolling="no"
                            style={{
                                display: 'block',
                                width: '1px',
                                minWidth: '100%',
                                height: '100%',
                                minHeight: '320px',
                                border: '0px none',
                                opacity: loading ? 0 : 1,
                                backgroundColor: iframeBackgroundColor,
                            }}
                            src={verify ? `${url}&retry=${retryCount}` : url}
                            title={title}
                            onLoad={() => {
                                if (verify) {
                                    setTimeout(veryfyFrame, 1000 * retryCount);
                                } else {
                                    setLoading(false);
                                }
                            }}
                            tabIndex={-1}
                        />
                    </div>
                </div>
            </Background>
        </div>
    );
}

export default PreviewPanel;
